import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import type { ChangeEvent, HTMLInputTypeAttribute } from 'react';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import { Input } from '@/components/new-ui/input';
import type { SelectOption } from '@/components/ui/select';
import { cn } from '@/lib/utils';

/**
 * Form fields of the analytics request form in the redesigned look. They are bound to
 * react-hook-form like the old FieldInput and FieldSelect, only the markup differs.
 * Everything here must be rendered inside the `.new-ui` scope.
 */

const labelClassName = 'text-sm font-medium leading-none';

const FieldError = ({ id, message }: { id: string; message?: string }) =>
  message ? (
    <p id={id} role="alert" className="text-xs text-destructive">
      {message}
    </p>
  ) : null;

const toFieldId = (name: string) => `analytics-${name.replace(/\./g, '-')}`;

export const RequestInput = <T extends FieldValues>({
  methods,
  name,
  label,
  type = 'text',
  step,
  decimals,
  maxLength,
  placeholder,
}: {
  methods: UseFormReturn<T>;
  name: Path<T>;
  label: string;
  type?: HTMLInputTypeAttribute;
  step?: number;
  decimals?: number;
  maxLength?: number;
  placeholder?: string;
}) => {
  const id = toFieldId(name);

  const handleChange = (onChange: (val: string | number) => void) => {
    return (ev: ChangeEvent<HTMLInputElement>) => {
      let value = ev.target.value;
      if (type !== 'number') {
        onChange(value);
        return;
      }
      value = value.trim().replace(',', '.');
      let [integerPart = '', decimalPart = ''] = value.split('.');

      if (decimals && decimals > 0 && decimalPart) {
        decimalPart = decimalPart.slice(0, decimals);
        value = `${integerPart}.${decimalPart}`;
      }

      if (maxLength && integerPart.length > maxLength) {
        integerPart = integerPart.slice(0, maxLength);
        value = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
      }

      onChange(value.length === 0 || Number.isNaN(Number(value)) ? '' : Number(value));
    };
  };

  return (
    <Controller
      {...methods}
      name={name}
      render={({ field: { onChange, value, ...field }, fieldState }) => (
        <div className="space-y-2">
          <label htmlFor={id} className={labelClassName}>
            {label}*
          </label>
          <Input
            {...field}
            id={id}
            type={type}
            step={step}
            placeholder={placeholder}
            value={value ?? ''}
            onChange={handleChange(onChange)}
            aria-invalid={fieldState.error ? true : undefined}
            aria-describedby={fieldState.error ? `${id}-error` : undefined}
          />
          <FieldError id={`${id}-error`} message={fieldState.error?.message} />
        </div>
      )}
    />
  );
};

export const RequestSelect = <T extends FieldValues>({
  methods,
  name,
  label,
  options = [],
  type = 'string',
  placeholder,
}: {
  methods: UseFormReturn<T>;
  name: Path<T>;
  label: string;
  options?: SelectOption[];
  type?: 'string' | 'number';
  placeholder?: string;
}) => {
  const id = toFieldId(name);

  return (
    <Controller
      {...methods}
      name={name}
      render={({ field: { onChange, value, ref, onBlur, disabled }, fieldState }) => {
        const stringValue = String(value ?? '');
        // A value without a matching option (for example a rating of 0, which means not
        // rated) shows the placeholder instead of an empty trigger; the form value stays
        const hasOption = options.some((option) => String(option.id) === stringValue);
        return (
          <div className="space-y-2">
            <label htmlFor={id} className={labelClassName}>
              {label}*
            </label>
            <SelectPrimitive.Root
              value={hasOption ? stringValue : ''}
              disabled={disabled}
              onValueChange={(next) => {
                // Inside a form Radix reports '' when the value is set before its option has
                // loaded; no option has an empty id, so this is never a real choice
                if (next === '') return;
                if (type === 'number') {
                  onChange(
                    next.trim().length === 0 || Number.isNaN(Number(next)) ? '' : Number(next)
                  );
                } else {
                  onChange(next);
                }
              }}
            >
              <SelectPrimitive.Trigger
                id={id}
                ref={ref}
                onBlur={onBlur}
                aria-invalid={fieldState.error ? true : undefined}
                aria-describedby={fieldState.error ? `${id}-error` : undefined}
                className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1"
              >
                <SelectPrimitive.Value placeholder={placeholder} />
                <SelectPrimitive.Icon asChild>
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </SelectPrimitive.Icon>
              </SelectPrimitive.Trigger>
              <SelectPrimitive.Portal>
                <SelectPrimitive.Content
                  position="popper"
                  className={cn(
                    'new-ui relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                    'data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1'
                  )}
                >
                  <SelectPrimitive.Viewport className="max-h-72 w-full min-w-[var(--radix-select-trigger-width)] p-1">
                    {options.map((option) => (
                      <SelectPrimitive.Item
                        key={option.id}
                        value={String(option.id)}
                        className="relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                      >
                        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                          <SelectPrimitive.ItemIndicator>
                            <Check className="h-4 w-4" />
                          </SelectPrimitive.ItemIndicator>
                        </span>
                        <SelectPrimitive.ItemText>{option.title}</SelectPrimitive.ItemText>
                      </SelectPrimitive.Item>
                    ))}
                  </SelectPrimitive.Viewport>
                </SelectPrimitive.Content>
              </SelectPrimitive.Portal>
            </SelectPrimitive.Root>
            <FieldError id={`${id}-error`} message={fieldState.error?.message} />
          </div>
        );
      }}
    />
  );
};
